export type Density = 'compact' | 'comfortable' | 'spacious' | 'filter';

export interface DensityValues {
  /**
   * The original density key. Primitives that need to override their own
   * `size` prop (e.g. `Button`) check this name to decide whether they're
   * inside a forcing region like `<FilterBar>`. Most primitives should
   * prefer the resolved numeric tokens below — this field exists only
   * for the rare case where a primitive must branch on the region itself.
   */
  name: Density;
  rowHeight: number;
  controlHeight: number;
  paddingY: string;
  paddingX: string;
  gap: string;
  fontSizeBump: number;
  iconSize: number;
  badgeFontSize: { xs: string; sm: string; md: string };
}

export const densityConfig: Record<Density, DensityValues> = {
  compact: {
    name: 'compact',
    rowHeight: 22,
    controlHeight: 24,
    paddingY: '2px',
    paddingX: '4px',
    gap: '3px',
    fontSizeBump: -1,
    iconSize: 12,
    badgeFontSize: { xs: '10px', sm: '11px', md: '12px' },
  },
  comfortable: {
    name: 'comfortable',
    rowHeight: 28,
    controlHeight: 32,
    paddingY: '4px',
    paddingX: '8px',
    gap: '6px',
    fontSizeBump: 0,
    iconSize: 14,
    badgeFontSize: { xs: '11px', sm: '12px', md: '13px' },
  },
  spacious: {
    name: 'spacious',
    rowHeight: 36,
    controlHeight: 40,
    paddingY: '8px',
    paddingX: '12px',
    gap: '8px',
    fontSizeBump: 1,
    iconSize: 16,
    badgeFontSize: { xs: '12px', sm: '13px', md: '14px' },
  },
  /**
   * The fixed 28 px filter-bar density. Used by `<FilterBar>` to force
   * uniform control heights regardless of the `size` prop a consumer
   * passes to a child primitive.
   */
  filter: {
    name: 'filter',
    rowHeight: 28,
    controlHeight: 28,
    paddingY: '4px',
    paddingX: '8px',
    gap: '6px',
    fontSizeBump: 0,
    iconSize: 14,
    badgeFontSize: { xs: '11px', sm: '12px', md: '13px' },
  },
} as const;
